import React, { useCallback, useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';

export default function CommunitySettings({
  communityId,
  governanceMode = false,
  onClose,
  onSaved,
}) {
  const { get, put } = useApi();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const params = communityId ? { communityId } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setForm(await get('/api/community-settings', { params }));
      setError('');
    } catch (e) {
      setForm(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [get, communityId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    try {
      await put('/api/community-settings', form, { params });
      setSaved(true);
      setError('');
      onSaved?.();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading)
    return <div className="engagement-empty">Loading privacy settings…</div>;
  if (!form)
    return (
      <div className="engagement-page">
        <Alert
          type="danger"
          message={error || 'Unable to load privacy settings.'}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={load}
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="engagement-page">
      <div className="engagement-heading">
        <div>
          <span>
            {governanceMode
              ? 'Community data governance'
              : 'Community administration'}
          </span>
          <h2>Privacy &amp; retention</h2>
          <p>
            Configure data visibility, retention and notification behavior.
            Purchased modules are managed once under Enabled services below.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Close
          </button>
        )}
      </div>
      {error && <Alert type="danger" message={error} />}
      {saved && (
        <Alert
          type="success"
          message="Privacy and retention settings saved."
          onClose={() => setSaved(false)}
        />
      )}
      <form
        className="settings-panel"
        style={{ maxWidth: 'none' }}
        onSubmit={submit}
      >
        <div className="settings-grid">
          <label>
            Timezone
            <input
              className="form-control"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </label>
          <label>
            Directory visibility
            <select
              className="form-select"
              value={form.directoryVisibility}
              onChange={(e) =>
                setForm({ ...form, directoryVisibility: e.target.value })
              }
            >
              <option value="COMMUNITY">Community</option>
              <option value="PRIVATE">Private</option>
            </select>
          </label>
          <label>
            Visitor visibility
            <select
              className="form-select"
              value={form.guestVisibility}
              onChange={(e) =>
                setForm({ ...form, guestVisibility: e.target.value })
              }
            >
              <option value="COMMUNITY_READ_ONLY">Community read-only</option>
              <option value="PRIVATE">Private</option>
            </select>
          </label>
          <label>
            Visitor retention days
            <input
              type="number"
              min="30"
              max="3650"
              className="form-control"
              value={form.visitorRetentionDays}
              onChange={(e) =>
                setForm({
                  ...form,
                  visitorRetentionDays: Number(e.target.value),
                })
              }
            />
          </label>
        </div>
        <Toggle
          label="Show resident phone numbers"
          description="Controls phone visibility for ordinary residents in Search Members and the resident directory. Community Admin and Super Admin retain access for administration. When disabled, the backend removes phone numbers from resident-facing search responses."
          checked={form.showResidentPhoneToResidents}
          onChange={(value) =>
            setForm({ ...form, showResidentPhoneToResidents: value })
          }
        />
        <Toggle
          label="Notice read receipts"
          checked={form.noticeReadReceiptsEnabled}
          onChange={(value) =>
            setForm({ ...form, noticeReadReceiptsEnabled: value })
          }
        />
        <Toggle
          label="Push notifications"
          checked={form.pushNotificationsEnabled}
          onChange={(value) =>
            setForm({ ...form, pushNotificationsEnabled: value })
          }
        />
        <Toggle
          label="Privacy Centre in member profiles"
          description="When enabled, members and community administrators can submit and track privacy or data requests from Profile. When disabled, the section is hidden for this community. Super Admin access remains available."
          checked={form.privacyCenterEnabled}
          onChange={(value) =>
            setForm({ ...form, privacyCenterEnabled: value })
          }
        />
        <button className="btn btn-primary">Save privacy settings</button>
      </form>
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label className="setting-toggle">
      <span>
        <strong>{label}</strong>
        {description && (
          <small className="d-block text-muted">{description}</small>
        )}
      </span>
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
